/**
 * @module app.interaction.DrawTrack
 */
import appInteractionTrack from '../interaction/Track.js';
import olFeature from 'ol/Feature.js';
import olGeomLineString from 'ol/geom/LineString.js';
import olInteractionDraw from 'ol/interaction/Draw.js';
import DrawEventType from './DrawEventType';
import {create2DLineStringFrom2Points} from './interactionutil.js';
import {smallScreen} from "../utils";

/**
 * Interaction to draw a track.
 */
class DrawTrack extends appInteractionTrack {
  constructor(options) {
    super(options);

    /**
     * @type {ol.Feature}
     * @private
     */
    this.firstPoint_ = null;

    /**
     * @type {ol.Feature}
     * @private
     */
    this.lastPoint_ = null;

    /**
     * @type {ol.interaction.Draw}
     * @private
     */
    this.drawInteraction_ = new olInteractionDraw({
      type: 'Point',
      style: (feature, resolution) => {
        if (smallScreen()) {
          return null;
        }
        if (typeof this.sketchStyle === 'function') {
          return this.sketchStyle(feature, resolution);
        }
        return this.sketchStyle;
      }
    });

    this.drawInteraction_.on(
      DrawEventType.DRAWEND, this.onDrawEnd_.bind(this), this
    );

    this.on('change:active', this.handleActiveChanged_, this
    );

  }

  /**
   * @private
   */
  handleActiveChanged_() {
    const active = this.getActive();
    this.drawInteraction_.setActive(active);
  }

  /**
   * @inheritDoc
   */
  setMap(map) {
    super.setMapWithLayers(map);

    const prevMap = this.drawInteraction_.getMap();
    if (prevMap) {
      prevMap.removeInteraction(this.drawInteraction_);
    }

    if (map) {
      map.addInteraction(this.drawInteraction_);
    }
  }

  /**
   * @param {ol.interaction.Draw.Event} evt
   * @private
   */
  onDrawEnd_(evt) {
    const feature = evt.feature;
    this.controlPoints.push(feature);
    this.refreshOverlays_(false);
  }

  /**
   * Remove the last control point.
   */
  removeLastPoint() {
    if (this.controlPoints.length === 0 || this.lastPoint_ === null) {
      return;
    }
    this.controlPoints.pop();
    if (this.segments.length > 0) {
      this.segments.pop();
      const segment = this.getFeatureProp(this.lastPoint_, 'segment');
      this.segmentSource.removeFeature(segment);
      this.dispatchSegmentChangeEvent();
    }
    this.refreshOverlays_(true);
  }

  /**
   * @param {boolean} removing
   * @private
   */
  refreshOverlays_(removing) {
    this.pointSource.clear();
    this.lastPoint_ = null;
    this.firstPoint_ = null;
    if (this.controlPoints.length > 0) {
      // always show the last control point
      const lastIndex = this.controlPoints.length - 1;
      const currentPoint = this.controlPoints[lastIndex];
      this.pointSource.addFeature(currentPoint);
      this.lastPoint_ = currentPoint;
      if (lastIndex !== 0) {
        const firstPoint = this.controlPoints[0];
        this.pointSource.addFeature(firstPoint);
        this.firstPoint_ = firstPoint;
      }

      if (!removing) {
        if (this.controlPoints.length >= 2) {
          const previousPoint = this.controlPoints[lastIndex - 1];
          this.addSegment_(previousPoint, currentPoint);
        }

        if (this.snap) {
          this.requestSnapPoint(currentPoint).then(
            this.snapPoint_.bind(this, currentPoint)
          );
        } else if (this.controlPoints.length >= 2) {
          this.dispatchSegmentChangeEvent();
        }
      }
    }
    this.dispatchChangeEvent();
  }

  /**
   * @param {ol.Feature} featureFrom
   * @param {ol.Feature} featureTo
   * @private
   */
  addSegment_(featureFrom, featureTo) {
    const pointFrom = this.getPointGeometry(featureFrom);
    const pointTo = this.getPointGeometry(featureTo);

    const segment = new olFeature({
      geometry: create2DLineStringFrom2Points(pointFrom, pointTo)
    });

    featureTo.set('previousPoint', featureFrom);
    featureTo.set('segment', segment);
    featureTo.set('snapped', false);

    this.segments.push(segment);
    this.segmentSource.addFeature(segment);
  }

  /**
   * @param {ol.Feature} feature
   * @param {ol.geom.Point} mappedPoint
   * @private
   */
  snapPoint_(feature, mappedPoint) {
    const point = this.getPointGeometry(feature);
    if (!this.isValidSnap(point, mappedPoint)) {
      if (this.controlPoints.length >= 2) {
        this.dispatchSegmentChangeEvent();
      }
      return;
    }
    feature.set('snapped', true);

    // use the mapped point
    feature.setGeometry(mappedPoint);

    if (feature.get('previousPoint')) {
      // update the segment leading to this point
      const previousFeature = this.getFeatureProp(feature, 'previousPoint');
      const previousPoint = this.getPointGeometry(previousFeature);
      feature.get('segment').setGeometry(new olGeomLineString([
        previousPoint.getCoordinates(),
        mappedPoint.getCoordinates()
      ]));

      // if the previous point was also snapped, we can try to find a route
      // between the points
      if (previousFeature.get('snapped') && feature.get('snapped')) {
        this.tryRequestRoute_(previousFeature, feature);
      } else {
        this.dispatchSegmentChangeEvent();
      }
    }
    this.dispatchChangeEvent();
  }

  /**
   * @param {ol.Feature} featureFrom
   * @param {ol.Feature} featureTo
   * @private
   */
  tryRequestRoute_(featureFrom, featureTo) {
    const pointFrom = this.getPointGeometry(featureFrom);
    const pointTo = this.getPointGeometry(featureTo);

    this.requestRoute(pointFrom, pointTo).then((route) => {
      featureTo.get('segment').setGeometry(route);
      this.dispatchSegmentChangeEvent();
      this.dispatchChangeEvent();
    });
  }
}


export default DrawTrack;
